/* eslint-disable import/no-unresolved */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import RightModalLayout from '../../../../../../../../layouts/Modal/RightModalLayout';
import CardMC from '../../../../../../../../components/CardMC';
import IconMC from '../../../../../../../../components/IconMC';
import WhatsappFlowMessageAndMappingConfig from '../../../../../../../WhatsappFlows/components/WhatsappFlowMessageAndMappingConfig';
import useTriggerWhatsappFlowToolConfig from '../../hooks/useTriggerWhatsappFlowToolConfig';

const STEP_LIST = 'list';
const STEP_CONFIG = 'config';

const TriggerWhatsappFlowToolView = ({
  t,
  footerSubmitLabel,
  handleBack,
  handleConfirmTriggerWhatsappFlow,
  flowItems,
  flowsLoading,
  selectedWhatsappFlowId,
  setSelectedWhatsappFlowId
}) => {
  const [step, setStep] = useState(STEP_LIST);
  const autoOpenedConfigRef = useRef(false);
  const flows = useMemo(() => flowItems || [], [flowItems]);
  const selectedFlow = useMemo(
    () => flows.find((f) => String(f.meta_flow_id || f.id) === String(selectedWhatsappFlowId)) || null,
    [flows, selectedWhatsappFlowId]
  );

  const flowConfig = useTriggerWhatsappFlowToolConfig({ selectedFlow });

  // When editing an existing tool with a flow already selected, open config once flows load.
  useEffect(() => {
    if (autoOpenedConfigRef.current) return;
    if (!selectedWhatsappFlowId || flowsLoading || !selectedFlow) return;
    autoOpenedConfigRef.current = true;
    setStep(STEP_CONFIG);
  }, [flowsLoading, selectedFlow, selectedWhatsappFlowId]);

  const selectFlowAndOpenConfig = (flowId) => {
    setSelectedWhatsappFlowId(flowId);
    setStep(STEP_CONFIG);
  };

  const goBackToList = () => {
    setStep(STEP_LIST);
  };

  const onConfirmConfig = async () => {
    if (!selectedWhatsappFlowId) return;
    const saved = await flowConfig.saveFlowConfiguration();
    if (!saved) return;
    handleConfirmTriggerWhatsappFlow();
  };

  if (step === STEP_CONFIG && selectedFlow) {
    return (
      <RightModalLayout
        closeAction={goBackToList}
        title={t('tools.trigger_whatsapp_flow_config_title')}
        caption={selectedFlow.name}
        showBackButton
        submitLabel={footerSubmitLabel}
        submitAction={onConfirmConfig}
        cancelLabel={t('Common:actions.cancel')}
        cancelAction={goBackToList}
        footerVariant="VERTICAL"
        submitDisabled={flowConfig.saving}
      >
        <FormProvider {...flowConfig.methods}>
          <WhatsappFlowMessageAndMappingConfig
            t={flowConfig.flowConfigT}
            showCustomerMapping={false}
            defaultBodyText={flowConfig.defaultBodyText}
            setDefaultBodyText={flowConfig.setDefaultBodyText}
            defaultFooterText={flowConfig.defaultFooterText}
            setDefaultFooterText={flowConfig.setDefaultFooterText}
            defaultFlowCta={flowConfig.defaultFlowCta}
            setDefaultFlowCta={flowConfig.setDefaultFlowCta}
          />
        </FormProvider>
      </RightModalLayout>
    );
  }

  return (
    <RightModalLayout
      closeAction={handleBack}
      title={t('tools.trigger_whatsapp_flow_title')}
      showBackButton
      cancelLabel={t('Common:actions.cancel')}
      cancelAction={handleBack}
      footerVariant="VERTICAL"
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-text-xs tw-text-gray-600">
          {t('tools.trigger_whatsapp_flow_picker_help')}
        </div>

        {flowsLoading && (
          <div className="tw-text-xs tw-text-gray-500">
            {t('tools.trigger_whatsapp_flow_loading')}
          </div>
        )}

        {!flowsLoading && flows.length === 0 && (
          <div className="tw-text-xs tw-text-gray-500 tw-py-4">
            {t('tools.trigger_whatsapp_flow_empty')}
          </div>
        )}

        <div className="tw-flex tw-flex-col tw-gap-2">
          {flows.map((flow) => {
            const flowId = String(flow.meta_flow_id || flow.id);
            const isSelected = selectedWhatsappFlowId === flowId;

            return (
              <CardMC
                key={flowId}
                className={
                  `tw-p-3 tw-flex tw-items-center tw-justify-between tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors ${
                    isSelected ? 'tw-bg-blue-50 !tw-border-blue-200' : ''
                  }`
                }
                onClick={() => selectFlowAndOpenConfig(flowId)}
              >
                <div className="tw-flex tw-gap-3 tw-items-center tw-min-w-0">
                  <div className="tw-p-2 tw-rounded-lg tw-shrink-0 tw-bg-blue-50">
                    <IconMC name="paper-plane-outline" className="tw-text-blue-500" />
                  </div>
                  <div className="tw-min-w-0">
                    <div className="tw-font-bold tw-text-sm tw-text-gray-900 tw-truncate">
                      {flow.default_header_text || flowId}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-truncate">
                      {flowId}
                    </div>
                  </div>
                </div>
                <IconMC name="chevron-right-fill" className="tw-text-gray-400 tw-shrink-0" />
              </CardMC>
            );
          })}
        </div>
      </div>
    </RightModalLayout>
  );
};

export default TriggerWhatsappFlowToolView;
